import { Box, Flex, IconButton, Text } from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { FiChevronLeft, FiChevronRight, FiLogOut } from "react-icons/fi"

import type { UserResponse } from "@/client"
import useAuth from "@/hooks/useAuth"
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerRoot,
} from "@chakra-ui/react"
import SidebarItems from "./SidebarItems"

interface SidebarProps {
  mobileOpen?: boolean
  onMobileOpenChange?: (open: boolean) => void
}

const Sidebar = ({ mobileOpen = false, onMobileOpenChange }: SidebarProps) => {
  const queryClient = useQueryClient()
  const currentUser = queryClient.getQueryData<UserResponse | null>([
    "auth",
    "currentUser",
  ])
  const { logout } = useAuth()
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Auto-collapse on medium screens (768px-1024px)
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      // Auto-collapse between md (768px) and lg (1024px) breakpoints
      setIsCollapsed(width >= 768 && width < 1024)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const handleDrawerOpenChange = (e: { open: boolean }) => {
    onMobileOpenChange?.(e.open)
  }

  return (
    <>
      {/* Mobile drawer - controlled by parent via mobileOpen prop */}
      <DrawerRoot
        placement="start"
        open={mobileOpen}
        onOpenChange={handleDrawerOpenChange}
      >
        <DrawerBackdrop />
        <DrawerContent maxW="xs">
          <DrawerCloseTrigger />
          <DrawerBody>
            <Flex flexDir="column" justify="space-between" h="full">
              <Box>
                {/* User info at top of mobile drawer */}
                {currentUser?.email && (
                  <Box
                    px={4}
                    py={3}
                    mb={2}
                    borderBottomWidth="1px"
                    borderColor="gray.200"
                  >
                    <Text fontSize="sm" fontWeight="medium" truncate>
                      {currentUser.email}
                    </Text>
                  </Box>
                )}
                <SidebarItems onClose={() => onMobileOpenChange?.(false)} />
                {/* Visual separator before logout */}
                <Box borderTopWidth="1px" borderColor="gray.200" mt={2} pt={2}>
                  <Flex
                    as="button"
                    onClick={() => {
                      logout()
                    }}
                    alignItems="center"
                    gap={4}
                    px={4}
                    py={3}
                    minH="44px"
                    w="full"
                    _hover={{ background: "gray.subtle" }}
                  >
                    <FiLogOut />
                    <Text>Log Out</Text>
                  </Flex>
                </Box>
              </Box>
            </Flex>
          </DrawerBody>
          <DrawerCloseTrigger />
        </DrawerContent>
      </DrawerRoot>

      {/* Desktop sidebar */}
      <Box
        display={{ base: "none", md: "flex" }}
        flexDir="column"
        position="sticky"
        bg="bg.subtle"
        top={0}
        w={isCollapsed ? "60px" : undefined}
        minW={isCollapsed ? "60px" : { md: "200px", lg: "240px", xl: "280px" }}
        maxW={isCollapsed ? "60px" : { md: "220px", lg: "260px", xl: "300px" }}
        h="100vh"
        p={isCollapsed ? 2 : { md: 2, lg: 3, xl: 4 }}
        transition="all 0.2s ease-in-out"
      >
        <Box w="100%" flex="1">
          <SidebarItems isCollapsed={isCollapsed} />
        </Box>
        {/* Collapse/Expand toggle button */}
        <IconButton
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(!isCollapsed)}
          alignSelf={isCollapsed ? "center" : "flex-end"}
          mt={2}
        >
          {isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
        </IconButton>
      </Box>
    </>
  )
}

export default Sidebar
